import React, { useEffect } from "react";
import "./App.css";
import "./index.css";
import "./styles/scrollbar.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";

// ============================================================================
// COMPONENT V2 & AUTH CONTEXT
// ============================================================================
import { AuthProvider as V2AuthProvider, useAuth as useV2Auth } from "./component_v2/context/AuthContext";
import Login from "./component_v2/Public/Login";
import Signup from "./component_v2/Public/Signup";
import VerifyEmail from "./component_v2/Public/VerifyEmail";
import ForgotPassword from "./component_v2/Public/ForgotPassword";
import ResetPassword from "./component_v2/Public/ResetPassword";
import VerifyOtp from "./component_v2/Public/VerifyOtp";
import DashboardLayout from "./component_v2/AuthenticatedApp/DashboardLayout";
import DashboardOverview from "./component_v2/AuthenticatedApp/DashboardOverview";
import CreateTrip from "./component_v2/AuthenticatedApp/CreateTrip";
import Profile from "./component_v2/AuthenticatedApp/Profile";
import ViewTrip from "./component_v2/AuthenticatedApp/ViewTrip";
import TripChangeRequestsView from "./component_v2/AuthenticatedApp/TripChangeRequestsView";
import MyTripsView from "./component_v2/AuthenticatedApp/MyTripsView";
import TripFeedbackView from "./component_v2/AuthenticatedApp/TripFeedbackView";
import ProtectedRoute from "./component_v2/AuthenticatedApp/ProtectedRoute";

// ============================================================================
// ORIGINAL PUBLIC & POLICY PAGES
// ============================================================================
import LandingPage from "./components/LandingPage";
import TermsAndServices from "./components/TermsAndServices";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Contact from "./components/Contact";
import Career from "./components/Career";
import AdminLogin from "./components/AdminLogin";

/**
 * PublicRoute helper: If already authenticated, redirect to ?redirect=... or /dashboard
 */
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useV2Auth();

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get("redirect") || "/dashboard";
    return <Navigate to={redirectUrl} replace />;
  }

  return children;
}

/**
 * Main Application
 */
function App() {
  useEffect(() => {
    return () => {
      document.body.classList.remove("mobile-menu-open");
    };
  }, []);

  return (
    <div className="App min-h-screen bg-[#070b14]">
      <V2AuthProvider>
        <Toaster />
        <BrowserRouter>
          <Routes>
            {/* Landing & Public Discovery Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/terms-and-services" element={<TermsAndServices />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Career />} />
            <Route path="/admin-login" element={<AdminLogin />} />

            {/* Public Authentication Routes with Original UI & Fresh Backend Logic */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />

            {/* Authenticated Dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DashboardOverview />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/create-trip"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreateTrip forcedMode="manual" />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/ai-planner"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreateTrip forcedMode="ai" />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Profile />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/my-trips"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <MyTripsView />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/feedback"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <TripFeedbackView />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/trip/:id"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ViewTrip />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/trip/:id"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ViewTrip />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/trip/:id/change-requests"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <TripChangeRequestsView />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/view-trip"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ViewTrip />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Catch-all fallback: redirect to Landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </V2AuthProvider>
    </div>
  );
}

export default App;
